import { type } from 'arktype';

// ---------------------------------------------------------------------------
// Document type constants
// ---------------------------------------------------------------------------

export const DOCUMENT_VISIBILITY = ['hr', 'hr+self', 'signed-only'] as const;
export type DocumentVisibility = (typeof DOCUMENT_VISIBILITY)[number];

export const PERSONAL_DOCUMENT_OWNER_TYPES = ['person', 'employment'] as const;
export type PersonalDocumentOwnerType = (typeof PERSONAL_DOCUMENT_OWNER_TYPES)[number];

export const PERSONAL_DOCUMENT_STATUSES = ['active', 'signed', 'archived', 'expired'] as const;
export type PersonalDocumentStatus = (typeof PERSONAL_DOCUMENT_STATUSES)[number];

/** Granularity of a structured retention duration. */
export const RETENTION_UNITS = ['year', 'month'] as const;
export type RetentionUnit = (typeof RETENTION_UNITS)[number];

/**
 * Structured retention duration: a magnitude (`interval`) and a granularity
 * (`unit`). `null` represents an unlimited retention / no legal hold.
 */
export interface RetentionDuration {
  interval: number;
  unit: RetentionUnit;
}

// Built-in document type IDs (stable keys, never change)
export const BUILT_IN_DOCUMENT_TYPES = ['contract', 'reference', 'receipt', 'policy', 'note', 'application'] as const;
export type BuiltInDocumentTypeId = (typeof BUILT_IN_DOCUMENT_TYPES)[number];

// Owner type for storage authorizer registration
export const PERSONAL_DOCUMENT_OWNER_TYPE = 'documents.personal-document';

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export interface DocumentTypeResponse {
  id: string;
  tenantId: string | null;
  label: string;
  defaultRetentionYears: number | null;
  /** Structured default retention; null = unlimited / no legal hold. */
  defaultRetention: RetentionDuration | null;
  isSystem: boolean;
  visibility: DocumentVisibility;
}

/**
 * Enriched document type shape for the admin catalog and per-type edit page.
 *
 * Adds the resolved {@link effectiveRetention} after applying the override
 * precedence chain (tenant override → type default → unlimited) and
 * {@link hasOverride}, which distinguishes "no tenant override" from an explicit
 * "no legal hold" override on a system type.
 */
export interface DocumentTypeDetailResponse extends DocumentTypeResponse {
  /** Resolved effective retention after precedence (null = unlimited). */
  effectiveRetention: RetentionDuration | null;
  /** Whether a tenant override row exists for this type. */
  hasOverride: boolean;
}

export interface PersonalDocumentResponse {
  id: string;
  tenantId: string;
  ownerType: PersonalDocumentOwnerType;
  ownerId: string;
  typeId: string;
  /**
   * Human-readable label for {@link typeId}, joined from `documents.document_types`
   * at read time. Falls back to {@link typeId} on the rare race condition where
   * the type was deleted between the read and the join (custom-only — system
   * types are never deletable).
   */
  typeLabel: string;
  title: string;
  description: string | null;
  fileId: string;
  /** Size of the underlying stored file in bytes. */
  fileSize: number;
  status: PersonalDocumentStatus;
  /** Validity window start (calendar date). Defaults to the upload date. */
  validFrom: Date;
  /** Validity window end / document expiration (calendar date). */
  validUntil: Date;
  retentionUntil: Date | null;
  approvalRunId: string | null;
  uploadedBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface RetentionOverrideResponse {
  tenantId: string;
  typeId: string;
  retentionYears: number | null;
  /** Structured override; null = explicit "no legal hold" override. */
  retention: RetentionDuration | null;
}

// ---------------------------------------------------------------------------
// Upload / create schema (ArkType)
// ---------------------------------------------------------------------------

/**
 * Validated payload for HR upload of a personal document.
 * Server derives tenantId, ownerType, ownerId from context.
 * Client supplies: typeId, title, description, target person/employment ID,
 * and the required validity window (validFrom / validUntil).
 */
export const uploadPersonalDocumentSchema = type({
  typeId: 'string >= 1',
  title: 'string >= 1',
  'description?': 'string | null',
  /** UUID of the target person. Server resolves ownerType from this. */
  personId: 'string >= 1',
  /** Validity start as ISO 8601 calendar date (YYYY-MM-DD). Defaults client-side to the upload date. */
  validFrom: type('string >= 1').configure({
    description: 'Validity window start as ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-06-18'],
  }),
  /** Validity end / document expiration as ISO 8601 calendar date (YYYY-MM-DD). Must be >= validFrom. */
  validUntil: type('string >= 1').configure({
    description: 'Validity window end / expiration as ISO 8601 calendar date (YYYY-MM-DD). Must be on or after validFrom.',
    examples: ['2036-06-18'],
  }),
});

export type UploadPersonalDocumentRequest = typeof uploadPersonalDocumentSchema.t;

export const UPLOAD_PERSONAL_DOCUMENT_BODY_KEYS = new Set(['typeId', 'title', 'description', 'personId', 'validFrom', 'validUntil']);

/**
 * Self-service upload payload. The target person is resolved from the
 * authenticated session, so `personId` is not accepted here. The validity
 * window is still required.
 *
 * Employees cannot override retention policy on their own uploads. The
 * document type's effective retention is always applied server-side.
 */
export const uploadOwnPersonalDocumentSchema = type({
  typeId: 'string >= 1',
  title: 'string >= 1',
  'description?': 'string | null',
  validFrom: type('string >= 1').configure({
    description: 'Validity window start as ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-06-18'],
  }),
  validUntil: type('string >= 1').configure({
    description: 'Validity window end / expiration as ISO 8601 calendar date (YYYY-MM-DD). Must be on or after validFrom.',
    examples: ['2036-06-18'],
  }),
});

export type UploadOwnPersonalDocumentRequest = typeof uploadOwnPersonalDocumentSchema.t;

export const UPLOAD_OWN_PERSONAL_DOCUMENT_BODY_KEYS = new Set(['typeId', 'title', 'description', 'validFrom', 'validUntil']);

// ---------------------------------------------------------------------------
// Archive schema
// ---------------------------------------------------------------------------

// Archive has no body; status flip is handled by endpoint path alone.

// ---------------------------------------------------------------------------
// Retention override update schema
// ---------------------------------------------------------------------------

/**
 * Structured retention override payload.
 *
 * `retention` is the new structured form: `{ interval, unit }` or `null` for
 * unlimited / no legal hold. `retentionYears` is accepted for back-compat with
 * older clients and is mapped to `{ interval: years, unit: 'year' }` server-side.
 * At least one of the two may be present; when both are present, `retention` wins.
 */
export const updateRetentionOverrideSchema = type({
  'retentionYears?': 'number >= 0 | null',
  'retention?': type({
    interval: 'number.integer >= 1',
    unit: "'year' | 'month'",
  }).or('null'),
});

export type UpdateRetentionOverrideRequest = typeof updateRetentionOverrideSchema.t;

export const UPDATE_RETENTION_OVERRIDE_BODY_KEYS = new Set(['retentionYears', 'retention']);

/**
 * Batch retention update payload — one structured override per document type.
 * Lets the admin UI persist every policy with a single save action.
 * Each entry's `retention` is `{ interval, unit }` or `null` (no legal hold).
 */
export const updateRetentionOverridesBatchSchema = type({
  policies: type({
    typeId: 'string >= 1',
    retention: type({
      interval: 'number.integer >= 1',
      unit: "'year' | 'month'",
    }).or('null'),
  }).array(),
});

export type UpdateRetentionOverridesBatchRequest = typeof updateRetentionOverridesBatchSchema.t;

export const UPDATE_RETENTION_OVERRIDES_BATCH_BODY_KEYS = new Set(['policies']);

// ---------------------------------------------------------------------------
// Document type CRUD schemas (ArkType)
// ---------------------------------------------------------------------------

/**
 * Structured retention duration accepted on document-type create/update.
 * `{ interval, unit }` sets a finite legal hold; `null` means unlimited /
 * no legal hold. Omitting the field on update leaves the current value.
 */
const documentTypeRetentionSchema = type({
  interval: 'number.integer >= 1',
  unit: "'year' | 'month'",
}).or('null');

/**
 * Payload for creating a tenant-managed document type.
 *
 * `id` is a stable, machine-readable slug, unique within the tenant. `label`
 * is the human-readable name. `visibility` controls who may read documents of
 * this type. `retention` is the structured default retention (null = unlimited).
 */
export const createDocumentTypeSchema = type({
  id: type('string >= 1 & string <= 64').configure({
    description: 'Stable slug identifier for the document type, unique per tenant.',
    examples: ['nda'],
  }),
  label: type('string >= 1 & string <= 200').configure({
    description: 'Human-readable label shown in the UI.',
    examples: ['Non-Disclosure Agreement'],
  }),
  'visibility?': type("'hr' | 'hr+self' | 'signed-only'").configure({
    description: "Who can read documents of this type. Defaults to 'hr' (most conservative).",
    examples: ['hr'],
  }),
  'retention?': documentTypeRetentionSchema.configure({
    description: 'Default retention duration ({ interval, unit }) or null for unlimited / no legal hold.',
    examples: [{ interval: 10, unit: 'year' }],
  }),
}).configure({ description: 'Payload for creating a tenant-managed document type.' });

export type CreateDocumentTypeRequest = typeof createDocumentTypeSchema.t;

export const CREATE_DOCUMENT_TYPE_BODY_KEYS = new Set(['id', 'label', 'visibility', 'retention']);

/**
 * Partial update for a document type.
 *
 * System (built-in) types only honour `retention`; their `label`/`visibility`
 * are immutable and a tenant's retention change is stored as a per-tenant
 * override rather than mutating the shared row. Custom tenant types honour all
 * fields directly.
 */
export const updateDocumentTypeSchema = type({
  'label?': type('string >= 1 & string <= 200').configure({
    description: 'Updated label. Ignored for system types.',
    examples: ['Non-Disclosure Agreement'],
  }),
  'visibility?': type("'hr' | 'hr+self' | 'signed-only'").configure({
    description: 'Updated visibility. Ignored for system types.',
    examples: ['hr+self'],
  }),
  'retention?': documentTypeRetentionSchema.configure({
    description: 'Updated default retention ({ interval, unit }) or null for unlimited / no legal hold.',
    examples: [{ interval: 5, unit: 'year' }],
  }),
}).configure({ description: 'Partial update for a document type. System types only honour retention.' });

export type UpdateDocumentTypeRequest = typeof updateDocumentTypeSchema.t;

export const UPDATE_DOCUMENT_TYPE_BODY_KEYS = new Set(['label', 'visibility', 'retention']);
